import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { Card, Divider } from '@material-ui/core';
import { get, find } from 'lodash';
import { useTranslation } from '../../../src/i18n';
import EditCardButtons from '../../../components/EditComponents/EditCardButtons';
import TitledBlock from '../../../components/TitledBlock';
import { MEDIA_TAGS_TYPES_IDS, TP, VIDEO_ATTRIBUTE_TYPES_IDS } from '../../../constants/consts';
import { dateShowFormat, getAttributeName, getTagsByType, getTimezonesOptions } from '../../../utils/common';
import ImageSet from '../../../components/ImageSet';
import DisablingCover from '../../../components/DisablingCover';
import DigitalPresenceLiveEventInfo from './DigitalPresenceLiveEventInfo';
import OuterLink from '../../../components/OuterLink';
import CostTypeChip from './CostTypeChip';

const useStyles = makeStyles(theme => ({
  card: {
    display: 'flex',
    marginBottom: theme.spacing(2),
    alignItems: 'center',
    position: 'relative',
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
    },
  },
  cardContent: {
    flex: 1,
    padding: theme.spacing(1),
  },
  cardInfo: {
    display: 'flex',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },
  poster: {
    marginRight: theme.spacing(1),
    minHeight: '3rem',
    maxHeight: 200,
    minWidth: '3rem',
    maxWidth: 200,
  },
  buttonsBlock: {
    marginTop: 20,
    marginBottom: 10,
  },
}));

const recordTitles = record => {
  const startDate = getAttributeName(record, VIDEO_ATTRIBUTE_TYPES_IDS.START_DATE);
  const endDate = getAttributeName(record, VIDEO_ATTRIBUTE_TYPES_IDS.END_DATE);
  return [
    { title: `${TP}.FN_TIME`, text: getAttributeName(record, VIDEO_ATTRIBUTE_TYPES_IDS.END_TIME) },
    { title: `${TP}.FN_AVAILABLE_FROM`, text: startDate ? dateShowFormat(startDate) : '' },
    { title: `${TP}.FN_AVAILABLE_TO`, text: endDate ? dateShowFormat(endDate) : '' },
    {
      title: `${TP}.REGISTRATION_TIME_ZONE`,
      text: find(getTimezonesOptions(), { value: getAttributeName(record, VIDEO_ATTRIBUTE_TYPES_IDS.TIMEZONE) })?.label,
    },
  ];
};

const VideoOnDemandEditCard = ({ record, onEdit, onDelete, loading }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const livePerformanceDateInfo = get(getTagsByType(record?.tags, MEDIA_TAGS_TYPES_IDS.DATE), '[0].productiondate');
  const costType = getAttributeName(record, VIDEO_ATTRIBUTE_TYPES_IDS.COST_TYPE);
  return (
    <Card className={classes.card}>
      {costType && <CostTypeChip label={costType} />}
      {record?.thumbnail && <ImageSet images={record?.thumbnail?.urls} className={classes.poster} />}
      <div className={classes.cardContent}>
        <OuterLink href={get(record, 'url')}>{t(`${TP}.FN_VIEW_URL`)}</OuterLink>
        <div className={classes.cardInfo}>
          {recordTitles(record)?.map(
            (info, index) =>
              info?.text && (
                <TitledBlock title={t(info?.title)} key={index}>
                  {info?.text}
                </TitledBlock>
              ),
          )}
        </div>
        {livePerformanceDateInfo && (
          <>
            <Divider />
            <DigitalPresenceLiveEventInfo liveEvent={livePerformanceDateInfo} />
          </>
        )}
      </div>
      <EditCardButtons editClick={onEdit} onDelete={onDelete} className={classes.buttonsBlock} />
      {loading && <DisablingCover />}
    </Card>
  );
};

VideoOnDemandEditCard.propTypes = {
  record: PropTypes.object.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};

VideoOnDemandEditCard.defaults = {
  loading: false,
};

export default VideoOnDemandEditCard;
